import { useAbbiamoUiConfig } from '@seller-hermes/app/config';
import { Badge } from '@seller-hermes/features/ui-v2/Badge';
import SellerChip from '@seller-hermes/features/ui-v2/seller-chip';
import { SellersQuery } from '@seller-hermes/features/sellers/queries/seller.query';
import { FetchKeys } from '@seller-hermes/features/ui/constants';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import SelectSellersDialog from './select-sellers-popup.component';
import { useSellersLookup } from './use-sellers-lookup';
import {
  Container,
  EmptySelectedData,
  Sellers,
  Title,
} from './sellers-selector.styles';
import { Seller } from './sellers-selector.types';

const MAX_SHOW_SELLERS_CHARS = 150;

interface ChildrenParams {
  setOpen: (open: boolean) => void;
  selectedSellers: Seller[];
  sellersToShow: Seller[];
  remainingSellersLength: number;
  sellersIDontHaveAccess: Seller[];
}

interface Props {
  onlyShowLength?: boolean;
  showTitle?: boolean;
  title?: string;
  description?: string;
  onSelectedSellersChange: (sellers: Seller[]) => void;
  selectedSellers: Seller[];
  children?: (params: ChildrenParams) => React.ReactNode;
  minimumAllowedSelectedSellers?: number;
}

const SellersSelector: React.FC<Props> = ({
  onlyShowLength = false,
  showTitle = true,
  selectedSellers,
  onSelectedSellersChange: setSelectedSellers,
  minimumAllowedSelectedSellers = 1,
  title,
  description,
  children,
}) => {
  const { getMe } = useAbbiamoUiConfig();
  const { data: me } = useQuery([FetchKeys.ME], getMe, {
    refetchOnWindowFocus: false,
    onSuccess(data) {
      if (selectedSellers.length === 0 && minimumAllowedSelectedSellers > 0) {
        setSelectedSellers(data.sellers);
      }
    },
  });

  // GET /v2/sellers/lookup (paginado, SUP-425): mesma projeção que o popup
  // consome (identifier, warehouses+address, seller_seller_tags) e MESMA
  // chave de cache do hook usado lá, então as duas telas enxergam
  // exatamente a mesma lista à medida que ela carrega em segundo plano.
  const { sellers: lookupSellers, hasLoadError: lookupHasLoadError } =
    useSellersLookup();

  const sellers = React.useMemo<Seller[]>(
    () =>
      lookupSellers.length > 0
        ? lookupSellers
        : (me?.sellers as unknown as Seller[]) || [],
    [lookupSellers, me],
  );

  // Fonte de verdade de ACESSO (SUP-425): antes isto diffava contra a lista
  // rica do lookup, que era buscada com `size: 1000` fixo — grupo com mais
  // de 1000 filiais tinha tudo que passasse do teto classificado como "sem
  // acesso" e re-anexado em toda seleção pelo handleSave do popup (grupo com
  // 1248 filiais → 248 grudadas). Agora a lista rica é paginada de propósito
  // (não pode mais ser "a lista completa"), então o diff usa
  // `lookupIds()` — payload mínimo, sem paginação, sempre completo.
  const { data: sellerIdsData } = useQuery(
    [FetchKeys.ME, 'sellers-lookup-ids'],
    () => SellersQuery.lookupIds(),
    // retry: 1 (SUP-425/426) — sem isso herda o default de 3 tentativas
    // com backoff do react-query, e essa query sozinha já pode travar o
    // popup (é ela quem decide "sem acesso" vs "inativa").
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, retry: 1 },
  );

  const sellersIDontHaveAccess = React.useMemo(() => {
    // Enquanto lookupIds() não voltou, usa me.sellers como aproximação em
    // vez de um Set vazio — senão toda seleção existente piscaria como "sem
    // acesso" por um instante no primeiro render.
    const sellerIds =
      sellerIdsData ?? ((me?.sellers as unknown as Seller[]) || []);
    const accessibleIds = new Set(sellerIds.map((s) => s.id));
    return selectedSellers.filter((seller) => !accessibleIds.has(seller.id));
  }, [selectedSellers, sellerIdsData, me]);

  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation('sellers-selector');

  const maxIndexToShow = React.useMemo(() => {
    return (
      selectedSellers.reduce(
        (acc, seller, index) => {
          acc.chars += seller.trading_name?.length || 0;
          if (acc.chars >= MAX_SHOW_SELLERS_CHARS && acc.maxIndex === null) {
            acc.maxIndex = index;
          }
          return acc;
        },
        { chars: 0, maxIndex: null as unknown as number },
      ).maxIndex || undefined
    );
  }, [selectedSellers]);

  const sellersToShow = React.useMemo(() => {
    return selectedSellers.slice(0, maxIndexToShow);
  }, [selectedSellers, maxIndexToShow]);

  const remainingSellersLength = React.useMemo(() => {
    return selectedSellers.length - maxIndexToShow!;
  }, [selectedSellers, maxIndexToShow]);

  const popup = (
    <SelectSellersDialog
      open={open}
      onOpenChange={setOpen}
      sellers={sellers}
      defaultValue={selectedSellers}
      onSelect={setSelectedSellers}
      title={title}
      description={description}
      sellersIDontHaveAccess={sellersIDontHaveAccess}
      minimumAllowedSelectedSellers={minimumAllowedSelectedSellers}
      hasLoadError={lookupHasLoadError}
    />
  );

  if (onlyShowLength)
    return (
      <React.Fragment>
        <div
          className="bg-muted mt-2 flex aspect-square w-full cursor-pointer items-center justify-center rounded-md border text-center text-xs"
          onClick={() => setOpen(true)}
        >
          {selectedSellers.length}
        </div>
        {popup}
      </React.Fragment>
    );

  return (
    <React.Fragment>
      {popup}
      {children ? (
        children({
          setOpen,
          selectedSellers,
          sellersToShow,
          remainingSellersLength,
          sellersIDontHaveAccess,
        })
      ) : (
        <Container>
          {showTitle && <Title>{t('title')}</Title>}
          <Sellers onClick={() => setOpen(true)}>
            {selectedSellers.length === 0 && (
              <EmptySelectedData>{t('empty-selected-data')}</EmptySelectedData>
            )}
            {sellersToShow.map((seller) => (
              <SellerChip
                baseStringToColor={seller.id}
                className="w-fit"
                key={seller.id}
              >
                {seller.trading_name}
              </SellerChip>
            ))}
            {remainingSellersLength > 0 && (
              <Badge variant="secondary" className="border-border">
                +{remainingSellersLength}
              </Badge>
            )}
          </Sellers>
        </Container>
      )}
    </React.Fragment>
  );
};

export default SellersSelector;
